/**
 * UWDSCRTab.jsx
 * Excel-replica of the "(UW) DSCR" tab in the Lender Workbook.
 * Columns: Tax Return (3 yrs), Internal/Interim, Projections Y1/Y2 (Borrower + Bank)
 * Rows mirror the exact layout shown in the Excel screenshots.
 */
import React, { useState, useCallback, useMemo } from "react";
import { cn, formatCurrency, formatMultiple, calcDSCR, dscrBadge, dscrLabel, dscrColor, annualize } from "../../lib/utils";

// ── Editable number cell ────────────────────────────────────────────────────
function NumCell({ value, onChange, readOnly = false, negative = false, bold = false }) {
  const [editing, setEditing] = useState(false);
  const [raw, setRaw] = useState("");

  const numValue = Number(value) || 0;
  const isNeg = numValue < 0 || negative;

  const display = formatCurrency(numValue);
  const displayClass = isNeg ? "uw-cell-negative" : numValue > 0 ? "uw-cell" : "uw-cell text-gray-400";

  if (readOnly) {
    return (
      <td className={cn("px-2 py-1 text-right font-mono text-sm tabular-nums border-r border-gray-200 min-w-[110px]",
        isNeg ? "text-red-600" : numValue !== 0 ? "text-gray-800" : "text-gray-400",
        bold && "font-bold"
      )}>
        {numValue !== 0 ? display : ""}
      </td>
    );
  }

  if (editing) {
    return (
      <td className="px-1 py-0.5 border-r border-gray-200 min-w-[110px]">
        <input
          autoFocus
          type="number"
          value={raw}
          className="uw-cell-input"
          onChange={e => setRaw(e.target.value)}
          onBlur={() => { onChange(Number(raw) || 0); setEditing(false); }}
          onKeyDown={e => { if (e.key === "Enter") { onChange(Number(raw) || 0); setEditing(false); } if (e.key === "Escape") setEditing(false); }}
        />
      </td>
    );
  }

  return (
    <td
      className={cn("px-2 py-1 cursor-pointer hover:bg-blue-50 border-r border-gray-200 min-w-[110px]",
        "text-right font-mono text-sm tabular-nums transition-colors",
        isNeg ? "text-red-600" : numValue !== 0 ? "text-gray-800" : "text-gray-300",
        bold && "font-bold"
      )}
      onClick={() => { setRaw(String(numValue || "")); setEditing(true); }}
      title="Click to edit"
    >
      {numValue !== 0 ? display : <span className="text-gray-300">—</span>}
    </td>
  );
}

// ── Period header colspan cell ─────────────────────────────────────────────
function PeriodHeader({ label, sub1, sub2, sub3, highlight }) {
  return (
    <th className={cn(
      "px-2 py-1 text-center text-xs font-semibold border-r border-gray-300",
      highlight ? "bg-[#dce6f1] text-[#17375e]" : "bg-[#1e3a5f] text-white"
    )}>
      <div className="font-bold">{label}</div>
      {sub1 && <div className="text-[10px] opacity-80 mt-0.5">{sub1}</div>}
      {sub2 && <div className="text-[10px] opacity-80">{sub2}</div>}
      {sub3 && <div className="text-[10px] opacity-60">{sub3}</div>}
    </th>
  );
}

// ── Row label cell ─────────────────────────────────────────────────────────
function RowLabel({ label, indent = 0, muted = false }) {
  return (
    <td className={cn(
      "px-3 py-1 text-xs whitespace-nowrap border-r border-gray-200 min-w-[200px] max-w-[240px]",
      "text-left sticky left-0 bg-white z-10",
      muted && "text-gray-400 italic"
    )} style={{ paddingLeft: `${12 + indent * 16}px` }}>
      {label}
    </td>
  );
}

// ── Section header row ─────────────────────────────────────────────────────
function SectionRow({ label, colCount = 9 }) {
  return (
    <tr>
      <td colSpan={colCount + 1} className="uw-row-header px-3 py-1.5 text-xs font-bold uppercase tracking-wide sticky left-0">
        {label}
      </td>
    </tr>
  );
}

// ── Total row ─────────────────────────────────────────────────────────────
function TotalRow({ label, values, colCount = 9, isMainTotal = false }) {
  return (
    <tr className={isMainTotal ? "uw-total-row" : "uw-subtotal-row"}>
      <td className={cn(
        "px-3 py-1.5 text-sm font-bold sticky left-0 border-r border-gray-300 min-w-[200px]",
        isMainTotal ? "bg-[#1e3a5f] text-white" : "bg-[#c6d9f1] text-[#1e3a5f]"
      )}>
        {label}
      </td>
      {values.map((v, i) => {
        const num = Number(v) || 0;
        return (
          <td key={i} className={cn(
            "px-2 py-1.5 text-right font-mono text-sm font-bold tabular-nums border-r border-gray-300 min-w-[110px]",
            isMainTotal ? "text-white" : num < 0 ? "text-red-700" : "text-[#1e3a5f]"
          )}>
            {num !== 0 ? formatCurrency(num) : ""}
          </td>
        );
      })}
    </tr>
  );
}

// ── DSCR ratio row ────────────────────────────────────────────────────────
function DSCRRow({ label, cashFlows, debtServices, stress = false }) {
  return (
    <tr className="border-t-2 border-[#1e3a5f]">
      <td className="px-3 py-1.5 text-xs font-bold bg-[#f0f7ff] sticky left-0 border-r border-gray-300 min-w-[200px]">
        {label}
      </td>
      {cashFlows.map((cf, i) => {
        const ds = debtServices[i] || 0;
        const dscr = calcDSCR(Number(cf) || 0, Number(ds) || 0);
        const badge = dscrBadge(dscr);
        return (
          <td key={i} className="px-2 py-1.5 text-center border-r border-gray-300 min-w-[110px]">
            {dscr !== null ? (
              <span className={cn("uw-cell font-bold text-sm", dscrColor(dscr))}>
                {formatMultiple(dscr)}
              </span>
            ) : <span className="text-gray-400 text-xs">N/A</span>}
          </td>
        );
      })}
    </tr>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main DSCR tab component
// ─────────────────────────────────────────────────────────────────────────────

const EMPTY_PERIOD = {
  salesRevenue: 0,
  netIncome: 0,
  interestAddback: 0,
  depreciationAddback: 0,
  rentAddback: 0,
  ownersCompensation: 0,
  salaryRequired: 0,
  rentalIncome: 0,
  distributions: 0,
  excessDistributions: 0,
  lossOnAssets: 0,
  otherAddback: 0,
};

const EMPTY_DEBT = {
  sevenASmallLoan: 0,
  usdaBnI: 0,
  fiveFourSeniorFB: 0,
  fiveFourSubordinateCDC: 0,
  sellersOtherNotes: 0,
};

const DEFAULT_PERIODS = [
  { type: "Tax Return",      date: "12/31/2022", months: 12, ...EMPTY_PERIOD, ...EMPTY_DEBT },
  { type: "Tax Return",      date: "12/31/2023", months: 12, ...EMPTY_PERIOD, ...EMPTY_DEBT },
  { type: "Tax Return",      date: "12/31/2024", months: 12, ...EMPTY_PERIOD, ...EMPTY_DEBT },
  { type: "Internal/Interim",date: "7/31/2025",  months: 7,  ...EMPTY_PERIOD, ...EMPTY_DEBT },
  { type: "Proj Y1 Borrower",date: "",           months: 12, ...EMPTY_PERIOD, ...EMPTY_DEBT },
  { type: "Proj Y2 Borrower",date: "",           months: 12, ...EMPTY_PERIOD, ...EMPTY_DEBT },
  { type: "Proj Y1 Bank",    date: "",           months: 12, ...EMPTY_PERIOD, ...EMPTY_DEBT },
  { type: "Proj Y2 Bank",    date: "",           months: 12, ...EMPTY_PERIOD, ...EMPTY_DEBT },
];

const INCOME_ROWS = [
  { key: "netIncome",          label: "Net Income", bold: true },
  { key: null,                 label: "Add Backs / Reductions:", section: true },
  { key: "interestAddback",    label: "Interest (+)",              indent: 1 },
  { key: "depreciationAddback",label: "Depreciation & Amortization (+)", indent: 1 },
  { key: "rentAddback",        label: "Rent (+)",                  indent: 1 },
  { key: "ownersCompensation", label: "Owner's Compensation (+)",   indent: 1 },
  { key: "salaryRequired",     label: "Salary Required (−)",        indent: 1, negate: true },
  { key: "rentalIncome",       label: "Rental Income (−)",          indent: 1, negate: true },
  { key: "distributions",      label: "Distributions (−)",          indent: 1, negate: true },
  { key: "excessDistributions",label: "Other: Excess Distributions (+)", indent: 1 },
  { key: "lossOnAssets",       label: "Other: Loss on Assets Sold", indent: 1 },
  { key: "otherAddback",       label: "Other:",                     indent: 1 },
];

const DEBT_ROWS = [
  { key: "sevenASmallLoan",         label: "7(a) or Small Loan" },
  { key: "usdaBnI",                 label: "USDA B&I Loan" },
  { key: "fiveFourSeniorFB",        label: "504 Senior Debt (FB)" },
  { key: "fiveFourSubordinateCDC",  label: "504 Subordinate Loan (SBA/CDC)" },
  { key: "sellersOtherNotes",       label: "Seller's & Other Notes" },
];

export default function UWDSCRTab({ initialData, borrowerName = "0", onDataChange }) {
  // Ensure we always have exactly 8 periods — merge saved data over DEFAULT_PERIODS
  // so that even an empty [] from the API doesn't crash the column headers.
  const [periods, setPeriods] = useState(() => {
    const saved = initialData?.periods;
    if (!saved || saved.length === 0) return DEFAULT_PERIODS;
    // Merge: fill missing slots with defaults
    return DEFAULT_PERIODS.map((def, i) => saved[i] ? { ...def, ...saved[i] } : def);
  });
  const [showDSCRwoDist, setShowDSCRwoDist] = useState(false);

  const updateCell = useCallback((pIdx, key, value) => {
    setPeriods(prev => {
      const next = prev.map((p, i) => i === pIdx ? { ...p, [key]: value } : p);
      onDataChange?.({ periods: next });
      return next;
    });
  }, [onDataChange]);

  // Derived calculations
  const totals = useMemo(() => periods.map(p => {
    const cf =
      (Number(p.netIncome) || 0) +
      (Number(p.interestAddback) || 0) +
      (Number(p.depreciationAddback) || 0) +
      (Number(p.rentAddback) || 0) +
      (Number(p.ownersCompensation) || 0) -
      (Number(p.salaryRequired) || 0) -
      (Number(p.rentalIncome) || 0) -
      (Number(p.distributions) || 0) +
      (Number(p.excessDistributions) || 0) +
      (Number(p.lossOnAssets) || 0) +
      (Number(p.otherAddback) || 0);
    const ds =
      (Number(p.sevenASmallLoan) || 0) +
      (Number(p.usdaBnI) || 0) +
      (Number(p.fiveFourSeniorFB) || 0) +
      (Number(p.fiveFourSubordinateCDC) || 0) +
      (Number(p.sellersOtherNotes) || 0);
    const cfWoDist = cf + (Number(p.distributions) || 0);
    return { cf, ds, cfWoDist };
  }), [periods]);

  // Safe period accessor — never throws even if periods is shorter than expected
  const p = (i) => periods[i] ?? DEFAULT_PERIODS[i] ?? {};

  const colHeaders = [
    { label: "Tax Return",         date: p(0).date, months: p(0).months },
    { label: "Tax Return",         date: p(1).date, months: p(1).months },
    { label: "Tax Return",         date: p(2).date, months: p(2).months },
    { label: "Internal/Interim",   date: p(3).date, months: p(3).months },
    { label: "Projections Year 1", sub: "12 / Borrower", months: 12 },
    { label: "Projections Year 2", sub: "12 / Borrower", months: 12 },
    { label: "Projections Year 1", sub: "12 / Bank",     months: 12, highlight: true },
    { label: "Projections Year 2", sub: "12 / Bank",     months: 12, highlight: true },
  ];

  return (
    <div className="h-full overflow-auto bg-white">
      {/* Borrower header bar */}
      <div className="flex items-center gap-4 px-4 py-2 bg-[#dce6f1] border-b border-gray-300 sticky top-0 z-20">
        <span className="text-sm font-bold text-[#1e3a5f] uppercase tracking-wide">Borrower:</span>
        <span className="text-sm font-mono text-[#1e3a5f] font-semibold">{borrowerName}</span>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-gray-600">Show DSCR w/o Distributions:</span>
          <button
            onClick={() => setShowDSCRwoDist(v => !v)}
            className={cn(
              "px-3 py-0.5 rounded text-xs font-bold border transition-colors",
              showDSCRwoDist ? "bg-[#1e3a5f] text-white border-[#1e3a5f]" : "bg-white text-gray-700 border-gray-400 hover:bg-gray-100"
            )}
          >
            {showDSCRwoDist ? "Yes" : "No"}
          </button>
        </div>
      </div>

      {/* Title */}
      <div className="px-4 py-2 bg-white border-b border-gray-200">
        <h3 className="text-sm font-bold text-[#1e3a5f] uppercase tracking-wide">
          Debt Service Coverage Ratio Analysis
        </h3>
      </div>

      {/* Main table */}
      <div className="overflow-x-auto">
        <table className="border-collapse w-full text-xs">
          <thead>
            <tr className="bg-[#1e3a5f]">
              {/* Sticky row label header */}
              <th className="px-3 py-1.5 text-left text-xs text-gray-300 sticky left-0 bg-[#1e3a5f] min-w-[200px] border-r border-gray-600">
                Type / Date / # Months
              </th>
              {colHeaders.map((col, i) => (
                <PeriodHeader
                  key={i}
                  label={col.label}
                  sub1={col.date || ""}
                  sub2={`# Months: ${col.months}`}
                  sub3={col.sub || ""}
                  highlight={col.highlight}
                />
              ))}
            </tr>
          </thead>

          <tbody>
            {/* ── Sales / Revenue ── */}
            <tr className="border-b border-gray-100 hover:bg-gray-50">
              <RowLabel label="Sales / Revenue" bold />
              {periods.map((p, i) => (
                <NumCell key={i} value={p.salesRevenue} onChange={v => updateCell(i, "salesRevenue", v)} />
              ))}
            </tr>

            {/* ── Spacer ── */}
            <tr className="h-2 bg-gray-50"><td colSpan={9}></td></tr>

            {/* ── Income rows ── */}
            {INCOME_ROWS.map(row => {
              if (row.section) return <SectionRow key={row.label} label={row.label} />;
              return (
                <tr key={row.key} className="border-b border-gray-100 hover:bg-gray-50">
                  <RowLabel label={row.label} indent={row.indent || 0} />
                  {periods.map((p, i) => (
                    <NumCell key={i} value={p[row.key]} onChange={v => updateCell(i, row.key, v)} />
                  ))}
                </tr>
              );
            })}

            {/* ── Total Cash Flow Available ── */}
            <TotalRow label="Total Cash Flow Available" values={totals.map(t => t.cf)} />

            {/* ── Spacer ── */}
            <tr className="h-3 bg-gray-50"><td colSpan={9}></td></tr>

            {/* ── Debt Service rows ── */}
            <SectionRow label="Annual Debt Service Payments" />
            {DEBT_ROWS.map(row => (
              <tr key={row.key} className="border-b border-gray-100 hover:bg-gray-50">
                <RowLabel label={row.label} />
                {periods.map((p, i) => (
                  <NumCell key={i} value={p[row.key]} onChange={v => updateCell(i, row.key, v)} />
                ))}
              </tr>
            ))}

            {/* ── Total Debt Service ── */}
            <TotalRow label="Total Debt Service Payments" values={totals.map(t => t.ds)} />

            {/* ── Spacer ── */}
            <tr className="h-3 bg-gray-50"><td colSpan={9}></td></tr>

            {/* ── Free Cash Flow ── */}
            <tr className="border-t-2 border-[#1e3a5f]">
              <td className="px-3 py-1.5 text-xs font-bold bg-[#f0f7ff] sticky left-0 border-r border-gray-300 min-w-[200px]">
                Free Cash Flow
              </td>
              {totals.map((t, i) => {
                const fcf = t.cf - t.ds;
                return (
                  <td key={i} className={cn(
                    "px-2 py-1.5 text-right font-mono text-sm font-bold tabular-nums border-r border-gray-300 min-w-[110px]",
                    fcf < 0 ? "text-red-600" : "text-green-700"
                  )}>
                    {t.ds !== 0 ? formatCurrency(fcf) : ""}
                  </td>
                );
              })}
            </tr>

            {/* ── DSCR ── */}
            <DSCRRow
              label="Debt Service Coverage Ratio"
              cashFlows={totals.map(t => showDSCRwoDist ? t.cfWoDist : t.cf)}
              debtServices={totals.map(t => t.ds)}
            />

            {/* ── Stress Test ── */}
            <tr className="border-t border-gray-300 bg-[#fff8e1]">
              <td className="px-3 py-1.5 text-xs font-bold sticky left-0 border-r border-gray-300 min-w-[200px] text-amber-800">
                Stress Test DSCR (Current Rate + 2.0%)
              </td>
              {totals.map((t, i) => {
                const dscr = calcDSCR(t.cf, t.ds * 1.05); // ~2% rate shock
                return (
                  <td key={i} className="px-2 py-1.5 text-center border-r border-gray-300 min-w-[110px]">
                    {t.ds !== 0 && dscr !== null ? (
                      <span className={cn("font-bold text-sm font-mono", dscrColor(dscr))}>
                        {formatMultiple(dscr)}
                      </span>
                    ) : <span className="text-gray-400 text-xs">N/A</span>}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 px-4 py-3 mt-4 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
        <div className="flex items-center gap-1.5">
          <span className="dscr-pass">PASS</span>
          <span>≥ 1.25x</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="dscr-warn">MARGINAL</span>
          <span>1.00x – 1.24x</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="dscr-fail">FAIL</span>
          <span>&lt; 1.00x</span>
        </div>
        <span className="ml-auto text-blue-600 font-medium">Click any cell to edit</span>
      </div>
    </div>
  );
}
